import { APP_INITIALIZER, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputNumberLegacyModule } from 'ng-zorro-antd/input-number-legacy';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzColorPickerModule } from 'ng-zorro-antd/color-picker';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRateModule } from 'ng-zorro-antd/rate';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzResultModule } from 'ng-zorro-antd/result';
import { LayoutModule } from '@angular/cdk/layout';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { BuilderScreenBetaComponent } from './builder-screen.component';
import { BuilderScreenBetaService } from './services/builder-screen.service';
import { BuilderValidationService } from './services/builder-validation.service';
import { InspectorConfigService } from './services/inspector-config.service';
import { UIStateService } from './services/ui-state.service';
import { ResponsiveLayoutService } from './services/responsive-layout.service';
import { PositionCalculatorService } from './floating-inspector/position-calculator.service';
import { PropertyGrouperService } from './floating-inspector/property-grouper.service';
import { PaletteBetaComponent } from './palette/palette.component';
import { CanvasBetaComponent } from './canvas/canvas.component';
import { InspectorBetaComponent } from './inspector/inspector.component';
import { InspectorDependenciesComponent } from './inspector/inspector-dependencies/inspector-dependencies.component';
import { JsonPreviewBetaComponent } from './json-preview/json-preview.component';
import { FloatingInspectorComponent } from './floating-inspector/floating-inspector.component';
import { PropertyControlComponent } from './floating-inspector/property-control.component';
import { PropertyPresetsModalComponent } from './floating-inspector/property-presets-modal.component';
import { DropdownOptionsModalComponent } from './inspector/dropdown-options-modal.component';
import { NumberFormatModalComponent } from './inspector/number-format-modal.component';
import { ContextFieldsModalComponent } from './inspector/context-fields-modal.component';
import { SearchDependencyModalComponent } from './inspector/search-dependency-modal.component';
import { FieldMappingModalComponent } from './inspector/field-mapping-modal.component';
import { AISuggestionsPanelComponent } from './ai-suggestions/ai-suggestions-panel.component';
import { InspectorModeToggleComponent } from './inspector-mode-toggle/inspector-mode-toggle.component';
import { StickyActionBarComponent } from './sticky-action-bar/sticky-action-bar.component';
import { InlineEditorComponent } from './inline-editor/inline-editor.component';
import { VirtualScrollPropertyListComponent } from './performance/virtual-scroll-property-list.component';
import { FeatureGuideComponent } from './help/feature-guide.component';
import { DragDropPropertyManagerService } from './floating-inspector/drag-drop-property-manager.service';
import { PropertyPresetsService } from './floating-inspector/property-presets.service';
import { AIPropertySuggestionsService } from './ai-suggestions/ai-property-suggestions.service';
import { AccessibilityService } from './accessibility/accessibility.service';
import { UIAnimationsService } from './animations/ui-animations.service';
import { PanelPresetSelectorComponent } from './panel-preset-selector/panel-preset-selector.component';
import { SmartSuggestionsPanelComponent } from './smart-suggestions-panel/smart-suggestions-panel.component';
import { WebhookAdapterBetaComponent } from './components/webhook-adapter/webhook-adapter.component';
import { BuilderSpreadsheetComponent } from './components/spreadsheet/spreadsheet.component';
import { WebhookCreatorModule } from '../webhook-creator/webhook-creator.module';
import { TemplateSelectorComponent } from './template-selector/template-selector.component';
import { TemplateService } from './services/template.service';
import { JsonEditorBetaComponent } from './json-editor/json-editor.component';
import { JsonTemplateIntegrationService } from './services/json-template-integration.service';
import { TemplateApiService } from './services/template-api.service';
import { ScreenLauncherComponent } from './screen-launcher/screen-launcher.component';
import { ScreenLauncherTestComponent } from './screen-launcher/screen-launcher-test.component';
import { TemplateDetailComponent } from './template-detail/template-detail.component';
import { LauncherApiService } from './services/launcher-api.service';
import { MasterDetailPanelComponent } from './components/master-detail-panel/master-detail-panel.component';
import { SharedModule } from '../shared-module/shared-module.module';
import { FlowConfigModalComponent } from './inspector/flow-config-modal/flow-config-modal.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DataGridComponent } from './components/data-grid/data-grid.component';
import { DataBindingService } from './services/data-binding.service';
import { MasterDetailService } from './services/master-detail.service';
import { SmartColumnBuilderComponent } from './components/smart-column-builder/smart-column-builder.component';
import { ColumnManagerComponent } from './components/column-manager/column-manager.component';
import { AutoScreenGeneratorComponent } from './components/auto-screen-generator/auto-screen-generator.component';

import { OptimizedAutoScreenGeneratorComponent } from './components/optimized-auto-screen-generator/optimized-auto-screen-generator.component';
import { FilterPipe } from './pipes/filter.pipe';
import { DataSourceConfigComponent } from './components/data-source-config/data-source-config.component';
// Event Scripting System
import { EventScriptEditorComponent } from './event-scripting/event-script-editor.component';
import { EventListComponent } from './event-scripting/event-list.component';
import { ExpertModeComponent } from './event-scripting/expert-mode.component';
import { SimpleModeComponent } from './event-scripting/simple-mode/simple-mode.component';
import { TemplateGalleryComponent } from './event-scripting/simple-mode/template-gallery.component';
import { TemplateConfiguratorComponent } from './event-scripting/simple-mode/template-configurator.component';
import { BehaviorPreviewComponent } from './event-scripting/simple-mode/behavior-preview.component';
import { NzMessageService } from 'ng-zorro-antd/message';

@NgModule({
  declarations: [
    BuilderScreenBetaComponent, 
    PaletteBetaComponent, 
    CanvasBetaComponent, 
    InspectorBetaComponent,
    InspectorDependenciesComponent, 
    JsonPreviewBetaComponent, 
    JsonEditorBetaComponent,
    WebhookAdapterBetaComponent,
    BuilderSpreadsheetComponent,
    DataGridComponent,
    SmartColumnBuilderComponent,
    ColumnManagerComponent,
    AutoScreenGeneratorComponent,
    OptimizedAutoScreenGeneratorComponent,
    FilterPipe,
    FloatingInspectorComponent,
    PropertyControlComponent,
    PropertyPresetsModalComponent,
    DropdownOptionsModalComponent,
    NumberFormatModalComponent,
    ContextFieldsModalComponent,
    SearchDependencyModalComponent,
    FieldMappingModalComponent,
    AISuggestionsPanelComponent,
    InspectorModeToggleComponent,
    StickyActionBarComponent,
    InlineEditorComponent,
    VirtualScrollPropertyListComponent,
    FeatureGuideComponent,
    PanelPresetSelectorComponent,
    SmartSuggestionsPanelComponent,
    TemplateSelectorComponent,
    ScreenLauncherComponent,
    ScreenLauncherTestComponent,
    TemplateDetailComponent,
    FlowConfigModalComponent,
    MasterDetailPanelComponent,
    DataSourceConfigComponent,
    // Event Scripting Components
    EventScriptEditorComponent,
    EventListComponent,
    ExpertModeComponent,
    SimpleModeComponent,
    TemplateGalleryComponent,
    TemplateConfiguratorComponent,
    BehaviorPreviewComponent
  ],
  imports: [
    ReactiveFormsModule, 
    FormsModule,
    ScrollingModule,
    DragDropModule,
    CommonModule,
    NzTabsModule,
    NzInputModule,
    NzFormModule,
    NzGridModule,
    NzListModule,
    NzButtonModule,
    NzInputNumberLegacyModule,
    NzSwitchModule,
    NzTableModule,
    NzTooltipModule,
    NzCheckboxModule,
    NzSelectModule,
    NzCollapseModule,
    NzTagModule,
    NzCardModule,
    NzEmptyModule,
    NzDividerModule,
    NzDatePickerModule,
    NzColorPickerModule,
    NzModalModule,
    NzDrawerModule,
    NzProgressModule,
    NzRateModule,
    NzRadioModule,
    NzBadgeModule,
    NzDropDownModule,
    NzLayoutModule,
    NzStepsModule,
    NzAlertModule,
    NzIconModule,
    NzSpaceModule,
    NzTypographyModule,
    NzDescriptionsModule,
    NzUploadModule,
    NzSpinModule,
    NzStatisticModule,
    NzResultModule,
    WebhookCreatorModule,
    SharedModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    NzButtonModule
  ],
  exports: [BuilderScreenBetaComponent, ScreenLauncherComponent, TemplateDetailComponent],
  providers: [
    BuilderScreenBetaService,
    BuilderValidationService,
    InspectorConfigService,
    UIStateService,
    ResponsiveLayoutService,
    PositionCalculatorService,
    PropertyGrouperService,
    DragDropPropertyManagerService,
    PropertyPresetsService,
    AIPropertySuggestionsService,
    AccessibilityService,
    UIAnimationsService,
    TemplateService,
    JsonTemplateIntegrationService,
    TemplateApiService,
    LauncherApiService,
    // DataBindingService,  // ❌ Removido: deve ser fornecido a nível de componente (screen-launcher)
    MasterDetailService,
    NzMessageService
  ]
})
export class BuilderScreenBetaModule {}



